import React, { useState, useEffect, useMemo } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import axios from 'axios';  // Global axios for defaults/interceptors
import apiClient from "./api/client";
import './App.css';
import { Toaster } from 'sonner';
import { PresenceManager } from './components/PresenceManager';
import ModalShell from './components/ModalShell';
import DegradedModeBanner from './components/DegradedModeBanner';
import { useDegradedMode } from './hooks/useDegradedMode';
import { installDebugErrorCapture } from './utils/debugSnapshot';

// Install debug error capture on app boot
installDebugErrorCapture();

// Build fingerprint - logs on app boot
const BUILD_TIMESTAMP = process.env.REACT_APP_BUILD_TIMESTAMP || 'dev';
const BUILD_COMMIT = process.env.REACT_APP_BUILD_COMMIT || 'local';
console.log(`%c🔧 UnityLove Build: ${BUILD_TIMESTAMP} • ${BUILD_COMMIT}`, 'color: #6366f1; font-weight: bold;');

// Import components
import LandingPage from './components/LandingPage';
import AuthPage from './components/AuthPage';
import Dashboard from './components/Dashboard';
import ProfileSetup from './components/ProfileSetup';
import Discover from './components/Discover';
import Matches from './components/Matches';
// Chat routing with feature flag for safe cutover
import ChatLegacy from './components/Chat';
import { ChatPage as ChatNew } from './components/chat';
// Feature flag: REACT_APP_USE_NEW_CHAT=true uses refactored ChatPage.jsx
const USE_NEW_CHAT = process.env.REACT_APP_USE_NEW_CHAT === 'true';
const Chat = USE_NEW_CHAT ? ChatNew : ChatLegacy;
import VideoChat from './components/VideoChat';
import FlirtCenter from './components/FlirtCenter';
import Premium from './components/Premium';
import WhoLikedMe from './components/WhoLikedMe';
import NotFound from './components/NotFound';
import HelpCenter from './components/HelpCenter';
import PrivacyPolicy from './components/PrivacyPolicy';
import InnerWorkPage from './pages/InnerWorkPage';
import RoomsPage from './pages/RoomsPage';
import RoomPage from './pages/RoomPage';
import TermsOfService from './components/TermsOfService';
import CommunityGuidelines from './components/CommunityGuidelines';
import SafetyCenter from './components/SafetyCenter';
import LifestyleLounge from './components/LifestyleLounge';
import ErrorBoundary from './components/ErrorBoundary';
import ProfilePreviewPlaceholder from './components/ProfilePreviewPlaceholder';
import ProfileRouteModalPage from './pages/ProfileRouteModalPage';
import Inbox from './components/Inbox';
import DebugOverlay from './components/DebugOverlay';
import AdminModerationConsole from './components/admin/AdminModerationConsole';
import AdminRoute from './components/admin/AdminRoute';
import ContactUsPage from './pages/ContactUsPage';
// ProfileEditor removed - using EditProfilePage as canonical editor
// import ProfileEditor from './components/ProfileEditor';
import InstallAppBanner from './components/InstallAppBanner';
import { GiftAnimationProvider } from './hooks/useGiftAnimations';
import { TypingProvider } from './context/TypingContext';
import { PostEventMomentumProvider } from './components/PostEventMomentumContext';
import { ThemeProvider } from './context/ThemeContext';
import { EntitlementsProvider } from './context/EntitlementsContext';
import { canUseVideoChat, getEliteUpgradeUrl } from './utils/entitlements';
import GiftLeaderboardsPage from './pages/GiftLeaderboardsPage';
import ElitePerksPage from './pages/ElitePerksPage';
import ElitePerksPageNew from './pages/ElitePerksPage.jsx';
import SpeedDatingEventsPage from './pages/SpeedDatingEventsPage';
import SpeedDatingLobbyPage from './pages/SpeedDatingLobbyPage';
import SpeedDatingHubPage from './pages/SpeedDatingHubPage';
import TextSpeedDatingPage from './pages/TextSpeedDatingPage';
import VideoSpeedDatingPage from './pages/VideoSpeedDatingPage';
import SpecialEventsPage from './pages/SpecialEventsPage';
// Demo components removed: PostEventMomentumDemo, VIPTicketDemo, CalendarDemo
import HowItWorksPage from './pages/HowItWorksPage';
import { VerifyAgePage } from './pages/VerifyAgePage';
import VerifyAgeExplainerPage from './pages/VerifyAgeExplainerPage';
import { EditProfilePage } from './pages/EditProfilePage';
import DiscoveryPreferencesPage from './pages/DiscoveryPreferencesPage';
import { BillingSuccess } from './pages/billing/BillingSuccess';
import { BillingCancelled } from './pages/billing/BillingCancelled';
import ReferralDashboard from './pages/ReferralDashboard.tsx';
import { LoveLinkHowPayoutsWork } from './pages/LoveLinkHowPayoutsWork.tsx';
import EarningsPage from './pages/EarningsPage';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminCompPage from './pages/admin/AdminCompPage';
import AdminLoveLinkEarnings from './pages/admin/AdminLoveLinkEarnings.tsx';
import AdminDisputesPage from './pages/admin/AdminDisputesPage';
import AdminReportsPage from './pages/admin/AdminReportsPage';
import AdminReportDetailPage from './pages/admin/AdminReportDetailPage';
import AdminModerationPage from './pages/admin/AdminModerationPage';
import AdminUser360Page from './pages/admin/AdminUser360Page';
import AdminAuditPage from './pages/admin/AdminAuditPage';
import AdminRoomReportsPage from './pages/admin/AdminRoomReportsPage';
import AdminEntitlementsOverride from './pages/admin/AdminEntitlementsOverride';
import AdminEntitlementsDebugPage from './pages/admin/AdminEntitlementsDebugPage';
import AdminRevenuePage from './pages/admin/AdminRevenuePage';
import AdminPayoutsPage from './pages/admin/AdminPayoutsPage';
import AdminValuePayoutsPage from './pages/admin/AdminValuePayoutsPage';
import AdminAnalyticsPage from './pages/admin/AdminAnalyticsPage';
import AdminAnalyticsV2Page from './pages/admin/AdminAnalyticsV2Page';
import AdminLoveCoinsPage from './pages/admin/AdminLoveCoinsPage';
import AdminSpeedDatingOverrides from './pages/admin/AdminSpeedDatingOverrides';
import AdminRoomsPage from './pages/admin/AdminRoomsPage';
import AdminRoomPromptsPage from './pages/admin/AdminRoomPromptsPage';
import AdminSpecialTopicsPage from './pages/admin/AdminSpecialTopicsPage';
import ReferralLanding from './pages/ReferralLanding';
import LoveYourWayEducation from './pages/LoveYourWayEducation';
import FamilyContinuityEducation from './pages/FamilyContinuityEducation';
import ResourcesMenu from './components/ResourcesMenu';
import GlobalFooter from './components/GlobalFooter';
import ScrollManager from './components/ScrollManager';
import ShopPage from './components/ShopPage';
import ConstellationScreen from './pages/ConstellationScreen';
import GuardianConsole from './pages/GuardianConsole';
import SettingsPage from './pages/SettingsPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import ConnectionsPage from './pages/ConnectionsPage';
import InvitePage from './pages/InvitePage';
import ComingSoonPage from './pages/ComingSoonPage';
import CommunitiesPage from './pages/CommunitiesPage';
import MessagesComingSoon from './pages/MessagesComingSoon';
import CoupleProfilePage from './pages/CoupleProfilePage';
import HerSpacePage from './pages/HerSpacePage';
import WomensCirclePage from './pages/WomensCirclePage';
import AdminCirclesPage from './pages/admin/AdminCirclesPage';
import QuickMatchPage from './pages/QuickMatchPage';
import ConnectionArcadePage from './pages/ConnectionArcadePage';
import ArcadeInvitePage from './pages/ArcadeInvitePage';

// Short /spark/Name-TOKEN redirector — extracts the token portion after the last dash
function SparkRedirect() {
  const { slug } = useParams();
  const [sp] = useSearchParams();
  // slug = "Unity-WsDhoQEi" → token = everything after last "-"
  // But the token itself may contain dashes, so split on first "-" only
  const dashIdx = slug ? slug.indexOf('-') : -1;
  const token = dashIdx > 0 ? slug.slice(dashIdx + 1) : slug;
  const ref = sp.get('ref') || '';
  return <Navigate to={`/arcade/invite/${token}${ref ? `?ref=${ref}` : ''}`} replace />;
}
import AdminAnalyticsSanityPage from './pages/AdminAnalyticsSanityPage';
import AdminAuthObservabilityPage from './pages/AdminAuthObservabilityPage';
import WingmanPage from './pages/WingmanPage';
import InitiativesPage from './pages/InitiativesPage';
import AdminInitiativesOpsPage from './pages/admin/AdminInitiativesOpsPage';
import EliteUpgradeModal from './components/common/EliteUpgradeModal';
import FeatureGate from './components/common/FeatureGate';

// SEO Landing Pages
import BlackPolyDatingPage from './pages/seo/BlackPolyDatingPage';
import BlackPolyamoryDatingAppPage from './pages/seo/BlackPolyamoryDatingAppPage';
import BlackPolygamyDatingPage from './pages/seo/BlackPolygamyDatingPage';
import BlackDatingAppPage from './pages/seo/BlackDatingAppPage';
import PolyDatingAppPage from './pages/seo/PolyDatingAppPage';
import EthicalNonMonogamyDatingPage from './pages/seo/EthicalNonMonogamyDatingPage';
import AboutUnityLovePage from './pages/seo/AboutUnityLovePage';
import WhySomeChoosePage from './pages/seo/WhySomeChoosePage';
// SEO Cluster - Sister Wife & Plural Family Building (built 2026-06-08)
import HowToFindSisterWifePage from './pages/seo/HowToFindSisterWifePage';
import SisterWifeDatingAppPage from './pages/seo/SisterWifeDatingAppPage';
import BlackSisterWifeDatingPage from './pages/seo/BlackSisterWifeDatingPage';
import PolyDatingForCouplesPage from './pages/seo/PolyDatingForCouplesPage';
import AfricanDiasporaDatingPage from './pages/seo/AfricanDiasporaDatingPage';
import GuidesHubPage from './pages/seo/GuidesHubPage';
// SEO Cluster Batch 2 (built 2026-06-09)
import SisterWivesPolyFamilyBuildingPage from './pages/seo/SisterWivesPolyFamilyBuildingPage';
import BuildingPolygamousFamilyPage from './pages/seo/BuildingPolygamousFamilyPage';
import PolyFamilyPlanningPage from './pages/seo/PolyFamilyPlanningPage';
import EthicalPolyamoryForBeginnersPage from './pages/seo/EthicalPolyamoryForBeginnersPage';
import SafePolyDatingPage from './pages/seo/SafePolyDatingPage';
// SEO Cluster Batch 3 - Identity / Community / Trust (built 2026-06-10)
import BlackLoveDatingAppPage from './pages/seo/BlackLoveDatingAppPage';
import IntentionalRelationshipsPage from './pages/seo/IntentionalRelationshipsPage';
import VerifiedDatingAppPage from './pages/seo/VerifiedDatingAppPage';
import AiMatchmakingAppPage from './pages/seo/AiMatchmakingAppPage';
import DatingAsACouplePage from './pages/seo/DatingAsACouplePage';
// SEO Cluster Batch 4 - City SEO (built 2026-06-10)
import AtlantaBlackDatingPage from './pages/seo/AtlantaBlackDatingPage';
import HoustonBlackDatingPage from './pages/seo/HoustonBlackDatingPage';
import MontgomeryDatingAppPage from './pages/seo/MontgomeryDatingAppPage';
// Authority Library - Research Reports (built 2026-02)
import ReportsHubPage from './pages/seo/ReportsHubPage';
import StateOfBlackRelationships2026Page from './pages/seo/StateOfBlackRelationships2026Page';
import StateOfBlackPolyFamilies2026Page from './pages/seo/StateOfBlackPolyFamilies2026Page';
import FamilyBuildingHandbookPage from './pages/seo/FamilyBuildingHandbookPage';
import HowPeopleActuallyFindSisterWifePage from './pages/seo/HowPeopleActuallyFindSisterWifePage';
import RelationshipTrendsAfricanDiasporaPage from './pages/seo/RelationshipTrendsAfricanDiasporaPage';
import MeditationWallPage from './pages/MeditationWallPage';
import DailyMirrorAffirmationPage from './pages/DailyMirrorAffirmationPage';
import MirrorPromptsPage from './pages/MirrorPromptsPage';
import EntitlementsDebugPanel from './components/debug/EntitlementsDebugPanel';

// Feature Flags for dev surfaces lockdown
const FEATURES = {
  comingSoon: process.env.REACT_APP_SHOW_COMING_SOON === "1",
  universe: process.env.REACT_APP_ENABLE_UNIVERSE === "1",
  lounge: process.env.REACT_APP_ENABLE_LOUNGE === "1",
  videoChat: process.env.REACT_APP_ENABLE_VIDEO_CHAT === "1",
  speedDating: process.env.REACT_APP_ENABLE_SPEED_DATING === "1",
  leaderboards: process.env.REACT_APP_ENABLE_LEADERBOARDS === "1",
  guardians: process.env.REACT_APP_ENABLE_GUARDIANS === "1",
  verifyAge: process.env.REACT_APP_ENABLE_VERIFY_AGE === "1",
};


// Configure axios defaults for better error handling
axios.defaults.timeout = 15000;
axios.defaults.withCredentials = false;

// NOTE: Dev request interceptor moved inside App component useEffect to prevent
// double-registration on hot-reload / StrictMode. See App() body.

// Session expiry flag to prevent multiple redirects
let isRedirectingToLogin = false;

// Auth endpoints that should NEVER trigger session-expired redirect
const AUTH_ENDPOINTS = [
  '/auth/login',
  '/auth/register',
  '/auth/forgot-password',
  '/auth/reset-password',
  '/auth/change-password',
  '/auth/login',
  '/auth/register',
  '/auth/forgot-password',
  '/auth/reset-password',
  '/auth/change-password',
];

const isAuthEndpoint = (url) => {
  if (!url) return false;
  return AUTH_ENDPOINTS.some((endpoint) => url.includes(endpoint));
};

const isOnAuthPage = () => {
  const path = window.location.pathname;
  return path === '/auth' || path === '/' || path === '/forgot-password' || path === '/reset-password';
};

// Auth context
export const AuthContext = React.createContext();

/**
 * DebugCrashRoute - Dev-only route that throws an error to test error boundary
 */
function DebugCrashRoute() {
  const searchParams = new URLSearchParams(window.location.search);
  const isInternalParam = searchParams.get('internal') === '1';
  const isDebugEnv = process.env.REACT_APP_DEBUG === '1';
  const isDevelopment = process.env.NODE_ENV !== 'production';

  const isPlaywright = typeof window !== 'undefined' && window.__PLAYWRIGHT__;
  const isInternalTest = typeof window !== 'undefined' && window.__INTERNAL_TEST__;
  const isManualDebug =
    typeof window !== 'undefined' && localStorage.getItem('UL_INTERNAL_DEBUG') === '1';

  const hasInternalMarker = isPlaywright || isInternalTest || isManualDebug;
  const isAllowed = isDevelopment || isDebugEnv || (isInternalParam && hasInternalMarker);

  if (!isAllowed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 text-white">
        <div className="text-center">
          <h1 className="text-4xl font-bold mb-4">404</h1>
          <p className="text-slate-400">Page not found</p>
        </div>
      </div>
    );
  }

  throw new Error('Intentional test crash from /debug/crash route');
}

/**
 * ✅ AppRoutes
 * Correct backgroundLocation routing pattern:
 * - Base Routes render the BACKGROUND location (Universe stays mounted)
 * - Modal Routes render the CURRENT location (Chat/Profile overlay)
 */
function AppRoutes({ user }) {
  const location = useLocation();
  const backgroundLocation = location.state?.backgroundLocation || null;

  return (
    <>
      {/* Base routes: render backgroundLocation when it exists */}
      <Routes location={backgroundLocation || location}>
        <Route path="/" element={user ? <Navigate to="/dashboard" /> : <LandingPage />} />
        <Route path="/auth" element={user ? <Navigate to="/dashboard" /> : <AuthPage />} />
        <Route
          path="/forgot-password"
          element={user ? <Navigate to="/dashboard" /> : <ForgotPasswordPage />}
        />
        <Route
          path="/reset-password"
          element={user ? <Navigate to="/dashboard" /> : <ResetPasswordPage />}
        />
        <Route
          path="/verify-email"
          element={user ? <Navigate to="/dashboard" /> : <VerifyEmailPage />}
        />

        {/* LoveLink referral landing */}
        <Route path="/r/:refCode" element={<ReferralLanding />} />

        {/* Trust-chain invite landing (social, not revenue) */}
        <Route path="/invite/:code" element={<InvitePage />} />

        {/* Coming Soon / Future Vision - Public marketing page */}
        <Route path="/coming-soon" element={<ComingSoonPage />} />
        
        {/* Communities & Regions - Public page for inclusion visibility */}
        <Route path="/communities" element={<CommunitiesPage />} />
        
        {/* Community Initiatives - Public page for social impact programs */}
        <Route path="/initiatives" element={<InitiativesPage />} />

        {/* SEO Landing Pages - Public, crawlable */}
        <Route path="/black-poly-dating" element={<BlackPolyDatingPage />} />
        <Route path="/black-polyamory-dating-app" element={<BlackPolyamoryDatingAppPage />} />
        <Route path="/black-polygamy-dating" element={<BlackPolygamyDatingPage />} />
        <Route path="/black-dating-app" element={<BlackDatingAppPage />} />
        <Route path="/poly-dating-app" element={<PolyDatingAppPage />} />
        <Route path="/ethical-non-monogamy-dating" element={<EthicalNonMonogamyDatingPage />} />
        <Route path="/about-unitylove" element={<AboutUnityLovePage />} />
        <Route path="/why-some-choose" element={<WhySomeChoosePage />} />

        {/* SEO Guides Hub + Sister Wife / Poly Family Cluster (built 2026-06-08) */}
        <Route path="/guides" element={<GuidesHubPage />} />
        <Route path="/how-to-find-a-sister-wife" element={<HowToFindSisterWifePage />} />
        <Route path="/sister-wife-dating-app" element={<SisterWifeDatingAppPage />} />
        <Route path="/black-sister-wife-dating" element={<BlackSisterWifeDatingPage />} />
        <Route path="/poly-dating-for-couples" element={<PolyDatingForCouplesPage />} />
        <Route path="/african-diaspora-dating" element={<AfricanDiasporaDatingPage />} />
        {/* SEO Batch 2 (2026-06-09) */}
        <Route path="/sister-wives-and-poly-family-building" element={<SisterWivesPolyFamilyBuildingPage />} />
        <Route path="/building-a-polygamous-family" element={<BuildingPolygamousFamilyPage />} />
        <Route path="/poly-family-planning" element={<PolyFamilyPlanningPage />} />
        <Route path="/ethical-polyamory-for-beginners" element={<EthicalPolyamoryForBeginnersPage />} />
        <Route path="/safe-poly-dating" element={<SafePolyDatingPage /> } />
        {/* SEO Batch 3 - Identity / Community / Trust (2026-06-10) */}
        <Route path="/black-love-dating-app" element={<BlackLoveDatingAppPage />} />
        <Route path="/dating-app-for-intentional-relationships" element={<IntentionalRelationshipsPage />} />
        <Route path="/verified-dating-app" element={<VerifiedDatingAppPage />} />
        <Route path="/ai-matchmaking-app" element={<AiMatchmakingAppPage />} />
        <Route path="/dating-as-a-couple" element={<DatingAsACouplePage />} />
        {/* SEO Batch 4 - City SEO (2026-06-10) */}
        <Route path="/atlanta-black-dating" element={<AtlantaBlackDatingPage />} />
        <Route path="/houston-black-dating" element={<HoustonBlackDatingPage />} />
        <Route path="/montgomery-dating-app" element={<MontgomeryDatingAppPage />} />
        {/* Authority Library - Research Reports (2026-02) */}
        <Route path="/reports" element={<ReportsHubPage />} />
        <Route path="/state-of-black-relationships-2026" element={<StateOfBlackRelationships2026Page />} />
        <Route path="/state-of-black-poly-families-2026" element={<StateOfBlackPolyFamilies2026Page />} />
        <Route path="/family-building-handbook" element={<FamilyBuildingHandbookPage />} />
        <Route path="/how-people-actually-find-a-sister-wife" element={<HowPeopleActuallyFindSisterWifePage />} />
        <Route path="/relationship-trends-african-diaspora" element={<RelationshipTrendsAfricanDiasporaPage />} />

        <Route path="/profile-setup" element={user ? <ProfileSetup /> : <Navigate to="/auth" />} />
        <Route path="/dashboard" element={user ? <Dashboard /> : <Navigate to="/auth" />} />
        <Route path="/discover" element={user ? <Discover /> : <Navigate to="/auth" />} />
        <Route path="/arcade" element={user ? <ConnectionArcadePage /> : <Navigate to="/auth" />} />
        <Route path="/arcade/invite/:token" element={<ArcadeInvitePage />} />
        <Route path="/spark/:slug" element={<SparkRedirect />} />
        <Route path="/quick-match" element={user ? <QuickMatchPage /> : <Navigate to="/auth" />} />
        <Route path="/matches" element={user ? <Matches /> : <Navigate to="/auth" />} />

        {/* IMPORTANT: These routes still exist as non-modal (direct deep links). */}
        <Route path="/chat/:matchId" element={user ? <Chat /> : <Navigate to="/auth" />} />
        <Route path="/profile/:id" element={user ? <ProfileRouteModalPage /> : <Navigate to="/auth" />} />
        
        {/* P4: Messaging Bridge - Removed (dead route) */}
        {/* Route path="/messages-coming-soon" - REMOVED */}

        {/* Video Chat - Feature gated (Anyone can receive calls) */}
        <Route 
          path="/video/:matchId" 
          element={
            user ? (
              <FeatureGate enabled={FEATURES.videoChat} redirectTo="/inbox">
                <VideoChat />
              </FeatureGate>
            ) : (
              <Navigate to="/auth" />
            )
          } 
        />
        <Route path="/flirts" element={user ? <FlirtCenter /> : <Navigate to="/auth" />} />
        
        {/* Legacy routes - Redirect to Shop */}
        <Route path="/premium" element={<Navigate to="/elite" replace />} />
        
        <Route path="/who-liked-me" element={user ? <WhoLikedMe /> : <Navigate to="/auth" />} />
        
        {/* Lifestyle Lounge - Feature gated */}
        <Route
          path="/lifestyle-lounge"
          element={
            user ? (
              <FeatureGate enabled={FEATURES.lounge} redirectTo="/discover">
                <ErrorBoundary>
                  <LifestyleLounge />
                </ErrorBoundary>
              </FeatureGate>
            ) : (
              <Navigate to="/auth" />
            )
          }
        />
        
        {/* Universe/Constellation - Feature gated */}
        <Route 
          path="/universe" 
          element={
            user ? (
              <FeatureGate enabled={FEATURES.universe} redirectTo="/discover">
                <ConstellationScreen />
              </FeatureGate>
            ) : (
              <Navigate to="/auth" />
            )
          } 
        />
        
        {/* Guardian Console - Admin + Feature gated */}
        <Route 
          path="/guardians" 
          element={
            user ? (
              <FeatureGate enabled={FEATURES.guardians && !!user?.is_admin} redirectTo="/dashboard">
                <GuardianConsole />
              </FeatureGate>
            ) : (
              <Navigate to="/auth" />
            )
          } 
        />

        <Route path="/inbox" element={user ? <Inbox /> : <Navigate to="/auth" />} />
        
        {/* Earnings & Payouts */}
        <Route path="/earnings" element={user ? <EarningsPage /> : <Navigate to="/auth" />} />
        
        {/* Meditation Wall - a room, not a tool */}
        <Route path="/meditation" element={user ? <MeditationWallPage /> : <Navigate to="/auth" />} />

        {/* Admin area */}
        <Route
          path="/admin"
          element={
            user ? (
              <AdminRoute>
                <AdminDashboard />
              </AdminRoute>
            ) : (
              <Navigate to="/auth" />
            )
          }
        />
        <Route
          path="/admin/lovelink/earnings"
          element={
            user ? (
              <AdminRoute>
                <AdminLoveLinkEarnings />
              </AdminRoute>
            ) : (
              <Navigate to="/auth" />
            )
          }
        />
        <Route path="/admin/moderation" element={user?.is_admin ? <AdminModerationPage /> : <Navigate to="/admin" />} />
        <Route path="/admin/moderation-legacy" element={user ? <AdminModerationConsole /> : <Navigate to="/auth" />} />
        <Route path="/admin/disputes" element={user?.is_admin ? <AdminDisputesPage /> : <Navigate to="/admin" />} />
        <Route path="/admin/reports" element={user?.is_admin ? <AdminReportsPage /> : <Navigate to="/admin" />} />
        <Route path="/admin/reports/:reportId" element={user?.is_admin ? <AdminReportDetailPage /> : <Navigate to="/admin" />} />
        <Route path="/admin/users/:userId" element={user?.is_admin ? <AdminUser360Page /> : <Navigate to="/admin" />} />
        <Route path="/admin/users/:userId/entitlements" element={user?.is_admin ? <AdminEntitlementsOverride /> : <Navigate to="/admin" />} />
        <Route path="/admin/comp" element={user?.is_admin ? <AdminCompPage /> : <Navigate to="/admin" />} />
        <Route path="/admin/audit" element={user?.is_admin ? <AdminAuditPage /> : <Navigate to="/admin" />} />
        <Route path="/admin/room-reports" element={user?.is_admin ? <AdminRoomReportsPage /> : <Navigate to="/admin" />} />
        <Route path="/admin/revenue" element={user?.is_admin ? <AdminRevenuePage /> : <Navigate to="/admin" />} />
        <Route path="/admin/payouts" element={user?.is_admin ? <AdminPayoutsPage /> : <Navigate to="/admin" />} />
        <Route path="/admin/value-payouts" element={user?.is_admin ? <AdminValuePayoutsPage /> : <Navigate to="/admin" />} />
        <Route path="/admin/analytics" element={user?.is_admin ? <AdminAnalyticsPage /> : <Navigate to="/admin" />} />
        <Route path="/admin/analytics-v2" element={user?.is_admin ? <AdminAnalyticsV2Page /> : <Navigate to="/admin" />} />
        <Route path="/admin/lovecoins" element={user?.is_admin ? <AdminLoveCoinsPage /> : <Navigate to="/admin" />} />
        <Route path="/admin/speeddating/overrides" element={user?.is_admin ? <AdminSpeedDatingOverrides /> : <Navigate to="/admin" />} />
        <Route path="/admin/initiatives" element={user?.is_admin ? <AdminInitiativesOpsPage /> : <Navigate to="/admin" />} />
        <Route path="/admin/analytics-sanity" element={user?.is_admin ? <AdminAnalyticsSanityPage /> : <Navigate to="/admin" />} />
        <Route path="/admin/entitlements-debug" element={user?.is_admin ? <AdminEntitlementsDebugPage /> : <Navigate to="/admin" />} />
        <Route path="/admin/rooms" element={user?.is_admin ? <AdminRoomsPage /> : <Navigate to="/admin" />} />
        <Route path="/admin/rooms/prompts" element={user?.is_admin ? <AdminRoomPromptsPage /> : <Navigate to="/admin" />} />
        <Route path="/admin/room-prompts" element={user?.is_admin ? <AdminRoomPromptsPage /> : <Navigate to="/admin" />} />
        <Route path="/admin/room-topics" element={user?.is_admin ? <AdminRoomPromptsPage /> : <Navigate to="/admin" />} />
        <Route path="/admin/special-topics" element={user?.is_admin ? <AdminSpecialTopicsPage /> : <Navigate to="/admin" />} />
        <Route path="/admin/circles" element={user?.is_admin ? <AdminCirclesPage /> : <Navigate to="/admin" />} />
        <Route
          path="/admin/auth-observability"
          element={
            user ? (
              <AdminRoute>
                <AdminAuthObservabilityPage />
              </AdminRoute>
            ) : (
              <Navigate to="/auth" />
            )
          }
        />

        {/* Legacy profile/edit route - redirect to canonical editor */}
        <Route path="/profile/edit" element={user ? <Navigate to="/edit-profile" replace /> : <Navigate to="/auth" />} />
        
        {/* Gift Leaderboards - Feature gated */}
        <Route
          path="/leaderboards/gifts"
          element={
            user ? (
              <FeatureGate 
                enabled={FEATURES.leaderboards} 
                redirectTo="/dashboard"
                featureName="Gift Leaderboards"
                showGatePage={true}
              >
                <GiftLeaderboardsPage />
              </FeatureGate>
            ) : (
              <Navigate to="/auth" />
            )
          }
        />
        
        {/* Elite Perks - Public page (works logged out and logged in) */}
        <Route path="/elite" element={<ElitePerksPageNew />} />
        {/* Canonical redirect: /elite/perks → /elite */}
        <Route path="/elite/perks" element={<Navigate to="/elite" replace />} />
        
        {/* Speed Dating - Feature gated */}
        <Route
          path="/events/speed-dating"
          element={
            user ? (
              <FeatureGate enabled={FEATURES.speedDating} redirectTo="/discover">
                <SpeedDatingEventsPage />
              </FeatureGate>
            ) : (
              <Navigate to="/auth" />
            )
          }
        />
        <Route
          path="/events/speed-dating/:eventId"
          element={
            user ? (
              <FeatureGate enabled={FEATURES.speedDating} redirectTo="/discover">
                <SpeedDatingLobbyPage />
              </FeatureGate>
            ) : (
              <Navigate to="/auth" />
            )
          }
        />
        
        {/* Speed Dating Hub - Single entry point for both modes */}
        <Route
          path="/speed-dating"
          element={
            user ? (
              <SpeedDatingHubPage />
            ) : (
              <Navigate to="/auth" />
            )
          }
        />

        {/* Text Speed Dating - Always Available */}
        <Route
          path="/text-speed-dating"
          element={
            user ? (
              <TextSpeedDatingPage />
            ) : (
              <Navigate to="/auth" />
            )
          }
        />

        {/* Video Speed Dating - Daily per Region (Verified-only) */}
        <Route
          path="/speed-dating/video"
          element={
            user ? (
              <VideoSpeedDatingPage />
            ) : (
              <Navigate to="/auth" />
            )
          }
        />

        {/* Speed Dating Lobby - Direct event link */}
        <Route
          path="/speed-dating/lobby/:eventId"
          element={
            user ? (
              <SpeedDatingLobbyPage />
            ) : (
              <Navigate to="/auth" />
            )
          }
        />

        {/* Special Events - Curated speed dating */}
        <Route
          path="/events/special"
          element={
            user ? (
              <FeatureGate enabled={FEATURES.speedDating} redirectTo="/discover">
                <SpecialEventsPage />
              </FeatureGate>
            ) : (
              <Navigate to="/auth" />
            )
          }
        />
        
        {/* Age Verification - Feature gated */}
        {/* /verify-age = explainer (gateway); /verify-age/start = Stripe Identity redirect */}
        <Route 
          path="/verify-age" 
          element={
            user ? (
              <FeatureGate enabled={FEATURES.verifyAge} redirectTo="/settings">
                <VerifyAgeExplainerPage />
              </FeatureGate>
            ) : (
              <Navigate to="/auth" />
            )
          } 
        />
        <Route 
          path="/verify-age/start" 
          element={
            user ? (
              <FeatureGate enabled={FEATURES.verifyAge} redirectTo="/settings">
                <VerifyAgePage />
              </FeatureGate>
            ) : (
              <Navigate to="/auth" />
            )
          } 
        />
        <Route path="/edit-profile" element={user ? <EditProfilePage /> : <Navigate to="/auth" />} />
        <Route
          path="/discovery-preferences"
          element={user ? <DiscoveryPreferencesPage /> : <Navigate to="/auth" />}
        />
        <Route path="/couple" element={user ? <CoupleProfilePage /> : <Navigate to="/auth" />} />
        <Route path="/couple/success" element={user ? <CoupleProfilePage /> : <Navigate to="/auth" />} />
        <Route path="/her-space" element={user ? <HerSpacePage /> : <Navigate to="/auth" />} />
        <Route path="/her-space/circles/:eventId" element={user ? <WomensCirclePage /> : <Navigate to="/auth" />} />
        <Route path="/settings" element={user ? <SettingsPage /> : <Navigate to="/auth" />} />
        <Route path="/connections" element={user ? <ConnectionsPage /> : <Navigate to="/auth" />} />
        <Route path="/inner-work" element={user ? <InnerWorkPage /> : <Navigate to="/auth" />} />
        <Route path="/daily-mirror" element={user ? <DailyMirrorAffirmationPage /> : <Navigate to="/auth" />} />
        <Route path="/mirror-prompts" element={user ? <MirrorPromptsPage /> : <Navigate to="/auth" />} />
        <Route path="/rooms" element={user ? <RoomsPage /> : <Navigate to="/auth" />} />
        <Route path="/rooms/:slug" element={user ? <RoomPage /> : <Navigate to="/auth" />} />

        {/* Billing & Shop */}
        <Route path="/billing/success" element={<BillingSuccess />} />
        <Route path="/billing/cancelled" element={<BillingCancelled />} />
        <Route path="/shop" element={user ? <ShopPage /> : <Navigate to="/auth" />} />
        <Route path="/wingman" element={user ? <WingmanPage /> : <Navigate to="/auth" />} />

        {/* LoveLink / Referrals - Canonical: /lovelink */}
        <Route path="/lovelink" element={user ? <ReferralDashboard /> : <Navigate to="/auth" />} />
        {/* Canonical redirects → /lovelink */}
        <Route path="/lovelinks/dashboard" element={<Navigate to="/lovelink" replace />} />
        <Route path="/referrals" element={<Navigate to="/lovelink" replace />} />
        <Route path="/lovelink/how-payouts-work" element={<LoveLinkHowPayoutsWork />} />
        {/* Canonical redirect → /lovelink/how-payouts-work */}
        <Route path="/lovelinks/how-payments-work" element={<Navigate to="/lovelink/how-payouts-work" replace />} />

        {/* Public pages */}
        <Route path="/help" element={<HelpCenter />} />
        <Route path="/contact" element={<ContactUsPage />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/guidelines" element={<CommunityGuidelines />} />
        {/* Canonical redirect: /community-guidelines → /guidelines */}
        <Route path="/community-guidelines" element={<Navigate to="/guidelines" replace />} />
        <Route path="/safety" element={<SafetyCenter />} />
        <Route path="/how-it-works" element={<HowItWorksPage />} />
        <Route path="/education/love-your-way" element={<LoveYourWayEducation />} />
        <Route path="/education/family-continuity" element={<FamilyContinuityEducation />} />

{/* Demo/Test routes removed */}
        {/* /demo/momentum, /demo/vip-ticket, /demo/calendar — components deleted */}

        {/* 🔴 REMOVED: Debug crash route - dev-only, not for production */}
        {/* <Route path="/debug/crash" element={<DebugCrashRoute />} /> */}

        {/* Admin-only: Legacy chat for safe cutover comparison */}
        <Route
          path="/admin/dev/chat-legacy/:matchId"
          element={
            user?.is_admin ? (
              <ChatLegacy />
            ) : (
              <Navigate to="/dashboard" />
            )
          }
        />

        {/* 404 */}
        <Route path="*" element={<NotFound />} />
      </Routes>

      {/* Modal routes: Render as overlay if backgroundLocation exists, otherwise render standalone */}
      {backgroundLocation ? (
        <Routes location={location}>
          <Route
            path="/chat/:matchId"
            element={user ? <ModalShell><Chat /></ModalShell> : null}
          />
          <Route
            path="/profile/:id"
            element={user ? <ProfileRouteModalPage /> : null}
          />
        </Routes>
      ) : null}
    </>
  );
}

// Auth timing instrumentation (dev only)
const DEBUG_TIMING = process.env.REACT_APP_DEBUG_TIMING === '1';
const logTiming = (label, ms) => {
  if (DEBUG_TIMING) {
    console.info(`[TIMING] ${label}: ${ms}ms`);
  }
};

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [debugVisible, setDebugVisible] = useState(false);
  
  // ============================================================================
  // PART A: AUTH BOOTSTRAP STATE MACHINE
  // ============================================================================
  // authStatus: "booting" | "authed" | "unauthed"
  // - "booting": Initial state, checking stored tokens
  // - "authed": User is authenticated with valid session
  // - "unauthed": No valid session, user needs to login
  const [authStatus, setAuthStatus] = useState('booting');
  
  // Prevent refresh storms - only one refresh attempt at a time
  const refreshInFlightRef = React.useRef(false);

  // ✅ Canonical session-expired handler used by BOTH axios systems
  useEffect(() => {
    window.__UL_handleSessionExpired = (detail = {}) => {
      if (isOnAuthPage()) return;
      if (isRedirectingToLogin) return;
      // Don't redirect during boot - let bootstrap handle it
      if (authStatus === 'booting') return;

      const hasToken = !!localStorage.getItem('token');
      if (!hasToken) return;

      console.log('[AUTH_SESSION_EXPIRED]', detail);

      isRedirectingToLogin = true;
      localStorage.removeItem('token');
      setToken(null);
      setUser(null);
      setAuthStatus('unauthed');

      // Keep this consistent everywhere:
      window.location.href = '/auth?reason=session_expired';

      setTimeout(() => {
        isRedirectingToLogin = false;
      }, 3000);
    };

    return () => {
      delete window.__UL_handleSessionExpired;
    };
  }, [authStatus]);

  // Response interceptor for 401 handling (session expiry)
  // CRITICAL: Register ONCE and eject on unmount to prevent stacking
  useEffect(() => {
    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const status = error?.response?.status;
        const url = error?.config?.url || '';

        if (isAuthEndpoint(url)) return Promise.reject(error);
        if (isOnAuthPage()) return Promise.reject(error);
        // Don't trigger session expired during boot
        if (authStatus === 'booting') return Promise.reject(error);

        // Only redirect on 401 (session expired)
        if (status === 401) {
          window.__UL_handleSessionExpired?.({ source: 'axios(global)', url, status });
        }

        return Promise.reject(error);
      }
    );

    // Cleanup: eject interceptor on unmount
    return () => {
      axios.interceptors.response.eject(interceptorId);
    };
  }, [authStatus]);

  // ============================================================================
  // BOOTSTRAP AUTH FLOW (A2)
  // Run on app load to determine auth state without causing flicker
  // ============================================================================
  useEffect(() => {
    const bootstrapAuth = async () => {
      const startTime = performance.now();
      console.info('[Auth] Bootstrap starting...');
      
      const storedToken = localStorage.getItem('token');
      
      // A2.2: No tokens -> unauthed
      if (!storedToken) {
        console.info('[Auth] No stored token, setting unauthed');
        setAuthStatus('unauthed');
        setLoading(false);
        logTiming('auth_bootstrap_ms', Math.round(performance.now() - startTime));
        return;
      }
      
      // A2.3: Token exists, validate it
      try {
        console.info('[Auth] Validating stored token...');
        const response = await apiClient.get(`/auth/me`, {
          headers: { Authorization: `Bearer ${storedToken}` },
          timeout: 10000,
        });
        
        // A2.3b: Token valid -> set session, authed
        setUser(response.data);
        setToken(storedToken);
        setAuthStatus('authed');
        console.info('[Auth] Bootstrap: Token valid, user loaded:', response.data?.id || 'none');

        // Dispatch streak event
        window.dispatchEvent(
          new CustomEvent('streakUpdated', {
            detail: { streak: response.data?.streak_current || 0 },
          })
        );
        
      } catch (error) {
        const status = error?.response?.status;
        console.warn('[Auth] Bootstrap: Token validation failed', { status, message: error?.message });
        
        // A2.3c: Token invalid/expired -> attempt refresh ONCE
        if (status === 401 && !refreshInFlightRef.current) {
          refreshInFlightRef.current = true;
          
          try {
            console.info('[Auth] Bootstrap: Attempting token refresh...');
            const refreshToken = localStorage.getItem('refresh_token');
            
            if (refreshToken) {
              const refreshResponse = await apiClient.post(`/auth/refresh`, {
                refresh_token: refreshToken
              }, { timeout: 10000 });
              
              const newToken = refreshResponse.data.access_token;
              localStorage.setItem('token', newToken);
              if (refreshResponse.data.refresh_token) {
                localStorage.setItem('refresh_token', refreshResponse.data.refresh_token);
              }
              
              // Fetch user with new token
              const userResponse = await apiClient.get(`/auth/me`, {
                headers: { Authorization: `Bearer ${newToken}` },
                timeout: 10000,
              });
              
              setUser(userResponse.data);
              setToken(newToken);
              setAuthStatus('authed');
              console.info('[Auth] Bootstrap: Refresh successful');
              
            } else {
              throw new Error('No refresh token available');
            }
            
          } catch (refreshError) {
            // A2.3c: Refresh failed -> clear tokens, unauthed
            console.warn('[Auth] Bootstrap: Refresh failed, clearing session');
            localStorage.removeItem('token');
            localStorage.removeItem('refresh_token');
            setToken(null);
            setUser(null);
            setAuthStatus('unauthed');
          } finally {
            refreshInFlightRef.current = false;
          }
          
        } else {
          // Non-401 error or refresh already in flight -> clear and unauth
          localStorage.removeItem('token');
          setToken(null);
          setUser(null);
          setAuthStatus('unauthed');
        }
      }
      
      setLoading(false);
      logTiming('auth_bootstrap_ms', Math.round(performance.now() - startTime));
    };

    bootstrapAuth();
  }, []); // Run only once on mount

  // Consume pending invite after login
  useEffect(() => {
    if (!user) return;
    const pending = localStorage.getItem('pending_invite_code');
    if (!pending) return;

    (async () => {
      try {
        const { ConnectionsAPI } = await import('./api/connectionsApi');
        await ConnectionsAPI.consumeInvite(pending);
        console.info('[App] Consumed pending invite:', pending);
      } catch (e) {
        console.warn('[App] Failed to consume pending invite:', e?.message);
      } finally {
        localStorage.removeItem('pending_invite_code');
      }
    })();
  }, [user]);

  // Debug overlay keyboard toggle (admin-only)
  useEffect(() => {
    function handler(e) {
      if (e.ctrlKey && e.altKey && e.key.toLowerCase() === 'd') {
        e.preventDefault();
        if (user?.is_admin) setDebugVisible((v) => !v);
      }
    }
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [user]);

  // Global refresh functions for badges and streak
  useEffect(() => {
    window.refreshNotificationSummary = async () => {
      try {
        if (window.refreshNotificationSummaryImpl) {
          await window.refreshNotificationSummaryImpl();
        }
        if (window.refreshDashboardBadges) {
          await window.refreshDashboardBadges();
        }
      } catch (e) {
        console.warn('[App] refreshNotificationSummary failed:', e);
      }
    };

    window.refreshStreakAndBadges = async () => {
      try {
        const t = localStorage.getItem('token');
        if (t) {
          const res = await apiClient.get(`/auth/me`, {
            headers: { Authorization: `Bearer ${t}` },
            timeout: 5000,
          });
          setUser(res.data);
          console.info('[App] Refreshed user data after GO task');

          window.dispatchEvent(
            new CustomEvent('streakUpdated', {
              detail: {
                streak: res.data?.streak_current || 0,
                go_count: res.data?.unity_flow_go_count || 0,
                completed_today: res.data?.unity_flow_completed_today || false,
              },
            })
          );
        }

        await window.refreshNotificationSummary?.();
      } catch (e) {
        console.warn('[App] refreshStreakAndBadges failed:', e);
      }
    };

    return () => {
      delete window.refreshNotificationSummary;
      delete window.refreshStreakAndBadges;
    };
  }, []);

  // Global handler for Ably lifecycle noise
  useEffect(() => {
    const handleUnhandledRejection = (event) => {
      const error = event.reason;
      const msg = String(error?.message || error || '').toLowerCase();
      const stack = String(error?.stack || '').toLowerCase();

      const isAbly =
        error?.constructor?.name === 'ErrorInfo' ||
        error?.statusCode != null ||
        msg.includes('ably') ||
        msg.includes('realtimechannel') ||
        stack.includes('realtimechannel') ||
        stack.includes('websockettransport');

      const isLifecycle =
        msg.includes('channel detached') ||
        msg.includes('channel is detached') ||
        msg.includes('connection closed') ||
        msg.includes('unable to recover connection') ||
        msg.includes('detach request superseded') ||
        msg.includes('attach request superseded');

      if (isAbly && isLifecycle) {
        console.warn('[Ably] Suppressed non-fatal lifecycle event:', error?.message || error);
        event.preventDefault();
      }
    };

    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    return () => window.removeEventListener('unhandledrejection', handleUnhandledRejection);
  }, []);

  // ============================================================================
  // A4: LOGIN FLOW - Order matters to prevent flicker
  // 1. Persist tokens FIRST
  // 2. Update auth context SECOND  
  // 3. Set authStatus="authed" THIRD
  // Navigation happens in calling component with { replace: true }
  // ============================================================================
  const login = async (newToken, refreshToken = null) => {
    // 1. Persist tokens to storage FIRST
    localStorage.setItem('token', newToken);
    if (refreshToken) {
      localStorage.setItem('refresh_token', refreshToken);
    }
    
    // 2. Fetch user data to populate context
    try {
      const response = await apiClient.get(`/auth/me`, {
        headers: { Authorization: `Bearer ${newToken}` },
        timeout: 10000,
      });
      
      // 3. Update auth context with user data
      setUser(response.data);
      setToken(newToken);
      
      // 4. Set authStatus="authed" LAST (before navigation)
      setAuthStatus('authed');
      
      console.info('[Auth] Login complete, user:', response.data?.id);
      
      // Dispatch streak event
      window.dispatchEvent(
        new CustomEvent('streakUpdated', {
          detail: { streak: response.data?.streak_current || 0 },
        })
      );
      
      return response.data;
    } catch (error) {
      console.error('[Auth] Login failed to fetch user:', error);
      // Still set token - user can be fetched on next request
      setToken(newToken);
      setAuthStatus('authed');
      return null;
    }
  };

  // ============================================================================
  // A5: LOGOUT FLOW
  // Clear tokens -> clear session -> set unauthed -> navigate
  // ============================================================================
  const logout = async () => {
    try {
      const { closeRealtimeClient } = await import('./services/realtime');
      await closeRealtimeClient();
    } catch (err) {
      console.error('[App] Error closing realtime on logout:', err);
    }

    // Clear all tokens
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    
    // Clear session state
    setToken(null);
    setUser(null);
    
    // Set authStatus to unauthed
    setAuthStatus('unauthed');
  };

  const debugExtraState = useMemo(() => `user=${user?.id || 'anon'} auth=${authStatus}`, [user?.id, authStatus]);
  const degradedMode = useDegradedMode();

  // ============================================================================
  // A3: LOADING/BOOTING STATE - Show loading screen during bootstrap
  // IMPORTANT: Never redirect during "booting" - this prevents flicker
  // ============================================================================
  if (loading || authStatus === 'booting') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-warm">
        <div className="text-center">
          <div className="text-2xl font-playfair text-rose-600 mb-2">Loading...</div>
          <div className="text-sm text-gray-500">Checking your session</div>
        </div>
      </div>
    );
  }

  return (
    <ThemeProvider>
      <GiftAnimationProvider>
        <TypingProvider>
          <AuthContext.Provider value={{ user, setUser, login, logout, token, authStatus }}>
            <EntitlementsProvider token={token}>
              <PresenceManager />
              <Toaster richColors position="top-center" />
              <div className="App min-h-screen flex flex-col">
                <DegradedModeBanner
                  degraded={degradedMode.degraded}
                  reason={degradedMode.reason}
                  lastError={degradedMode.lastError}
                  nextRetryAt={degradedMode.nextRetryAt}
                  onRetry={degradedMode.retry}
                />

                <HelmetProvider>
                  <BrowserRouter>
                    <PostEventMomentumProvider>
                      <ScrollManager />
                      <EliteUpgradeModal />
                      <div className="flex-grow">
                        <AppRoutes user={user} />
                      </div>

                      {user && <ResourcesMenu variant="floating" />}
                      <GlobalFooter />
                    </PostEventMomentumProvider>
                  </BrowserRouter>
                </HelmetProvider>

                <DebugOverlay
                  visible={debugVisible}
                  onClose={() => setDebugVisible(false)}
                  extraState={debugExtraState}
                  streakInfo={
                    user
                      ? {
                          streak_current: user.streak_current,
                          streak_best: user.streak_best,
                          streak_last_date: user.streak_last_date,
                          streak_server_date: user.streak_server_date,
                          streak_already_counted: user.streak_already_counted,
                          unity_flow_go_count: user.unity_flow_go_count,
                          unity_flow_tasks_done: user.unity_flow_tasks_done,
                          unity_flow_completed_today: user.unity_flow_completed_today,
                        }
                      : null
                  }
                />

                {/* Entitlements Debug Panel - dev/admin only */}
                {user && <EntitlementsDebugPanel />}

                <InstallAppBanner isLoggedIn={!!user} />
              </div>
            </EntitlementsProvider>
          </AuthContext.Provider>
        </TypingProvider>
      </GiftAnimationProvider>
    </ThemeProvider>
  );
}

export default App;
